'use client'

import { useState } from 'react'
import { useTranslations } from 'next-intl'
import { Combobox } from '@base-ui/react/combobox'
import { LoaderCircle } from 'lucide-react'
import { useAddressSuggestions } from '@/hooks/use-address-suggestions'
import { formatAddressSuggestion } from '@/lib/address-suggestions'
import type { Dealer, DealerMapSearchResult } from '@/types/dealers'
import { formatDealer } from '@/lib/dealers'
import { Input } from '@/app/[locale]/(ui)/input'

export const DealerMapSearch = ({
  dealers,
  onChange,
  onClear,
}: {
  dealers: Dealer[]
  onChange: (result: DealerMapSearchResult) => void
  onClear: () => void
}) => {
  const t = useTranslations()
  const address = useAddressSuggestions()
  const [searchValue, setSearchValue] = useState('')
  const searchQuery = searchValue.trim().toLowerCase()
  const isLoading = address.isLoading && searchQuery.length > 0

  const dealerItems: DealerMapSearchResult[] = dealers
    .filter((dealer) =>
      [dealer.name, dealer.address, dealer.postalCode, dealer.city].join(' ').toLowerCase().includes(searchQuery),
    )
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((dealer, index) => ({
      id: `${dealer.name}-${index}`,
      type: 'dealer',
      label: formatDealer(dealer),
      latitude: dealer.location.latitude,
      longitude: dealer.location.longitude,
      dealer,
    }))

  const addressItems: DealerMapSearchResult[] = address.suggestions.slice(0, 5).map((suggestion, index) => ({
    id: `${suggestion.adressetekst}-${index}`,
    type: 'address',
    label: formatAddressSuggestion(suggestion),
    latitude: suggestion.representasjonspunkt.lat,
    longitude: suggestion.representasjonspunkt.lon,
  }))

  const items = [
    dealerItems.length > 0 && { value: t('Dealers'), items: dealerItems },
    addressItems.length > 0 && { value: t('Addresses'), items: addressItems },
  ].filter(Boolean)

  const onInputValueChange = (value: string) => {
    setSearchValue(value)
    if (value === '') {
      address.setSuggestions([])
      onClear()
    }
    address.setValue(value)
  }

  const onValueChange = (result: DealerMapSearchResult | null) => {
    if (!result) return
    onChange(result)
  }

  return (
    <div>
      <Combobox.Root
        items={items}
        inputValue={searchValue}
        onInputValueChange={onInputValueChange}
        onValueChange={onValueChange}
        itemToStringLabel={(result: DealerMapSearchResult) => result.label}
        isItemEqualToValue={(item: DealerMapSearchResult, value: DealerMapSearchResult) => item.id === value.id}
        filter={null}
      >
        <Combobox.Input
          render={<Input type="search" label={t('Dealer_search_label')} placeholder={t('Dealer_search_placeholder')} />}
        />
        <Combobox.Portal>
          <Combobox.Positioner className="outline-none" sideOffset={4}>
            <Combobox.Popup
              className="max-h-[13rem] w-[var(--anchor-width)] max-w-[var(--available-width)] origin-[var(--transform-origin)] overflow-hidden rounded-md border border-black bg-white text-gray-900 shadow-lg shadow-gray-200"
              aria-busy={isLoading}
            >
              {isLoading && (
                <div className="flex items-center gap-2 px-4 py-3 text-sm">
                  <LoaderCircle size={18} className="animate-spin" />
                  <span>{t('Loading')}</span>
                </div>
              )}
              {!isLoading && (
                <Combobox.Empty>
                  {searchQuery && <div className="px-4 py-3 text-sm text-gray-600">{t('No_search_results')}</div>}
                </Combobox.Empty>
              )}
              <Combobox.List className="max-h-[min(13rem,var(--available-height))] scroll-py-2 overflow-y-auto overscroll-contain py-2 outline-0 data-[empty]:p-0">
                {(group) => (
                  <Combobox.Group key={group.value} items={group.items} className="block">
                    <Combobox.GroupLabel className="px-4 py-1 text-sm">{group.value}</Combobox.GroupLabel>
                    <Combobox.Collection>
                      {(result: DealerMapSearchResult) => (
                        <Combobox.Item
                          key={result.id}
                          value={result}
                          className="flex cursor-default items-center gap-2 px-4 py-2 text-sm outline-none data-[highlighted]:bg-black/10"
                        >
                          {result.type === 'dealer' ? (
                            <i className="flex size-[16px] shrink-0 rounded-full border-2 border-black bg-isekk" />
                          ) : (
                            <i className="flex size-[16px] shrink-0 rounded-full border-2 border-black bg-orange-500" />
                          )}
                          <span className="truncate">{result.label}</span>
                        </Combobox.Item>
                      )}
                    </Combobox.Collection>
                  </Combobox.Group>
                )}
              </Combobox.List>
            </Combobox.Popup>
          </Combobox.Positioner>
        </Combobox.Portal>
      </Combobox.Root>
    </div>
  )
}
